import { Elements, ExpressCheckoutElement, useElements } from '@stripe/react-stripe-js';
import { useContext, useMemo, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum } from '@/types/common';
import { SkeletonLoader } from '../Common/SkeletonLoader';
import {
  type AvailablePaymentMethods,
  type StripeExpressCheckoutElementConfirmEvent,
  type StripeExpressCheckoutElementReadyEvent,
  type StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js';
import { Loader } from '../Common/Loader';
import { APPLE_PAY_WALLET, GOOGLE_PAY_WALLET } from '@/constants/stripe';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { logError } from '@/utils/logger';
import { ErrorLogs } from '@/constants/logs';
import { getLogInfoData } from '@/utils/logs';
import { isStripeExpressPaymentMethodExists } from '@/utils/stripe';
import type { ExpressCheckoutComponentProps } from '@/utils/expressPayment';
import { trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';

const ExpressCheckoutButton = ({ onReady }: ExpressCheckoutComponentProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { runPayment },
    paymentMethodsAvailable,
  } = context;

  const elements = useElements();
  const [isElementReady, setIsElementReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [availableMethods, setAvailableMethods] = useState<AvailablePaymentMethods>({} as AvailablePaymentMethods);
  const [hasCheckedAvailability, setHasCheckedAvailability] = useState(false);

  const expressCheckoutOptions = useMemo(() => {
    const hasApplePay = isStripeExpressPaymentMethodExists(paymentMethodsAvailable, PaymentMethodEnum.APPLE_PAY);
    const hasGooglePay = isStripeExpressPaymentMethodExists(paymentMethodsAvailable, PaymentMethodEnum.GOOGLE_PAY);

    return {
      paymentMethods: {
        applePay: hasApplePay ? ('always' as const) : ('never' as const),
        googlePay: hasGooglePay ? ('always' as const) : ('never' as const),
        link: 'never' as const,
      },
      layout: {
        overflow: 'never' as const,
        maxColumns: 1 as const,
      },
      paymentMethodOrder: [APPLE_PAY_WALLET, GOOGLE_PAY_WALLET],
    } as StripeExpressCheckoutElementOptions;
  }, [paymentMethodsAvailable]);

  const hasAvailableMethods = useMemo(() => Object.values(availableMethods).some(Boolean), [availableMethods]);

  const handleReady = (e: StripeExpressCheckoutElementReadyEvent) => {
    const methods = e.availablePaymentMethods ?? ({} as AvailablePaymentMethods);
    setAvailableMethods(methods);
    setIsElementReady(true);
    setHasCheckedAvailability(true);
    onReady?.(Object.values(methods).some(Boolean));
  };

  const handleLoadError = () => {
    logError(ErrorLogs.expressCheckoutError, getLogInfoData(context));
    setIsElementReady(false);
    setAvailableMethods({} as AvailablePaymentMethods);
    setHasCheckedAvailability(true);
    onReady?.(false);
  };

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    try {
      setIsProcessing(true);
      const paymentMethod =
        event.expressPaymentType === 'apple_pay' ? PaymentMethodEnum.APPLE_PAY : PaymentMethodEnum.GOOGLE_PAY;

      await runPayment({ event, elements: elements! }, paymentMethod);
    } catch {
      console.error('PayButtonByDevice - Payment error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (hasCheckedAvailability && !hasAvailableMethods) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 relative">
      {isProcessing && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-50 rounded-xl">
          <Loader />
        </div>
      )}

      {(hasAvailableMethods || !hasCheckedAvailability) && (
        <div
          className={`transition-opacity duration-300 ${!hasCheckedAvailability ? 'opacity-0 absolute pointer-events-none' : 'opacity-100'} express-checkout-vertical`}
        >
          <ExpressCheckoutElement
            onClick={(event) => {
              if (!isElementReady) {
                setIsProcessing(true);
              }
              const paymentMethod =
                event.expressPaymentType === 'apple_pay' ? PaymentMethodEnum.APPLE_PAY : PaymentMethodEnum.GOOGLE_PAY;
              trackAndNotifyPaymentClick(context, paymentMethod);
              event.resolve({ emailRequired: true });
            }}
            onConfirm={handleConfirm}
            onReady={handleReady}
            onLoadError={handleLoadError}
            options={expressCheckoutOptions}
          />
        </div>
      )}
    </div>
  );
};

export const PayButtonByDevice = ({ onReady }: ExpressCheckoutComponentProps) => {
  const { pricing, paymentMethodsAvailable } = useContext(CheckoutContext);
  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({
    paymentMethod: isStripeExpressPaymentMethodExists(paymentMethodsAvailable, PaymentMethodEnum.GOOGLE_PAY)
      ? PaymentMethodEnum.GOOGLE_PAY
      : PaymentMethodEnum.APPLE_PAY,
    isExpress: true,
  });

  const shouldShowExpressCheckout = useMemo(
    () =>
      isStripeExpressPaymentMethodExists(paymentMethodsAvailable, PaymentMethodEnum.GOOGLE_PAY) ||
      isStripeExpressPaymentMethodExists(paymentMethodsAvailable, PaymentMethodEnum.APPLE_PAY),
    [paymentMethodsAvailable]
  );

  if (!shouldShowExpressCheckout) {
    return null;
  }

  if (isStripeLoading || !stripe) {
    return (
      <div className="flex flex-col gap-4">
        <SkeletonLoader />
      </div>
    );
  }

  return (
    <Elements
      stripe={stripe}
      options={{
        mode: 'payment',
        amount: Math.round(pricing.priceBreakdown.priceTotal),
        currency: pricing.currencyCode.toLowerCase(),
        loader: 'never',
        paymentMethodCreation: 'manual',
      }}
    >
      <ExpressCheckoutButton onReady={onReady} />
    </Elements>
  );
};
